import React, { ReactElement, useEffect, useState } from 'react'
import Autocomplete, { AutocompleteRenderInputParams, AutocompleteChangeReason } from '@mui/material/Autocomplete'
import { CircularProgress, TextField, SvgIcon } from '@mui/material'
import { IAutocompleteBase } from './types'
import ErrorMessage from '../../ErrorMessage'
import InputAdornment from '@mui/material/InputAdornment'
import Close from '@tdcerhverv/parrotfish/dist/icons/Icons/Close.svg'
import Search from '@tdcerhverv/parrotfish/dist/icons/Icons/Search.svg'
import PopOverInfo from '../../PopOverInfo'
import Box from '@mui/material/Box'
import usePropStyles from '../../utils/usePropStyles'
import { useController } from 'react-hook-form'

const styles = {
  noOptions: {
    display: 'none',
  },
  clearIndicator: {
    '& > span > svg ': {
      padding: '4px',
    },
  },
  endAdornment: {
    top: 'unset',
  },
  input: {
    height: '30px',
    padding: '0px !important',
  },
  boxStyles: {
    display: 'flex',
    paddingBottom: '24px',
    '& > button': {
      alignSelf: 'baseline',
    },
    '& .MuiInputBase-root': {
      backgroundColor: 'common.white',
    },
  },
}

export default function AutocompleteBase<T>({
  urlBuilder,
  modelData,
  minSearchTermLength = 3,
  onError,
  onChange,
  autoHighlight = true,
  autoComplete = true,
  getOptionLabel,
  getOptionSelected,
  fieldType: _fieldType,
  inputRef,
  autoSelect = true,
  disabled = false,
  required,
  popover,
  control,
  fieldWidth,
  assistiveText,
  ...props
}: IAutocompleteBase<T>): ReactElement {
  const [searchTerm, setSearchTerm] = useState<string>(props.value ?? '')
  const [loadingState, setLoadingState] = useState<boolean>(false)
  const [options, setOptions] = useState<T[]>([])
  const [open, setOpen] = useState<boolean>(false)
  const propStyles = usePropStyles(fieldWidth)
  const fieldName = props.name!
  const { field, fieldState } = useController({ name: fieldName, control, rules: { required: required! } })
  const hasError = Boolean(fieldState.error)

  useEffect(() => {
    if (!searchTerm || searchTerm.length < minSearchTermLength) {
      return
    }
    const controller = new AbortController()
    const signal = controller.signal
    const url = urlBuilder(searchTerm)

    setLoadingState(true)
    fetch(url, { signal })
      .then(response => response.json())
      .then(data => {
        const result = modelData(data)
        setOptions(result)
        setLoadingState(false)
      })
      .catch(error => {
        if (onError) {
          onError(error)
        }
        console.error(error)
      })

    return () => {
      setLoadingState(false)
      controller.abort()
    }
  }, [searchTerm])

  useEffect(() => {
    if (!open) {
      setOptions([])
      setLoadingState(false)
    }
  }, [open])

  const renderInput = (params: AutocompleteRenderInputParams) => {
    return (
      <TextField
        {...props}
        {...params}
        name={props.name!}
        placeholder={props.placeholder!}
        required={required!}
        id={fieldName}
        error={hasError}
        inputRef={inputRef}
        sx={propStyles}
        helperText={
          hasError ? (
            <ErrorMessage>
              <>{fieldState.error?.message}</>
            </ErrorMessage>
          ) : (
            assistiveText
          )
        }
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <SvgIcon fontSize="inherit" component={Search} viewBox="0 0 48 48" />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {loadingState ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )
  }
  return (
    <Box sx={styles.boxStyles}>
      <Autocomplete<T>
        {...field}
        defaultValue={field.value}
        options={options}
        renderInput={renderInput}
        onInputChange={(_event, newInputValue): void => {
          setSearchTerm(newInputValue)
        }}
        sx={[styles, propStyles]}
        onChange={(_event: React.SyntheticEvent, value: T | null, _reason: AutocompleteChangeReason) => {
          field.onChange(value)
          onChange && onChange(value)
        }}
        open={props.open || open}
        onOpen={() => {
          setOpen(true)
        }}
        onClose={() => {
          setOpen(false)
        }}
        loading={loadingState}
        autoHighlight={autoHighlight}
        autoComplete={autoComplete}
        autoSelect={autoSelect}
        popupIcon={<></>}
        clearIcon={
          <SvgIcon
            component={Close}
            fontSize="inherit"
            viewBox={'0 0 48 48'}
            sx={{ width: '14px !important', height: '14px !important', m: '4px' }}
          />
        }
        selectOnFocus={true}
        filterOptions={x => x}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        disabled={disabled!}
        blurOnSelect
      />
      {popover && <PopOverInfo {...popover} />}
    </Box>
  )
}
